import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

export type BurndownSeries = { color: string; dashed?: boolean; id: string; label: string; values: number[] };

const WIDTH = 720;
const HEIGHT = 320;
const MARGIN = { bottom: 32, left: 40, right: 16, top: 16 };
const PLOT_WIDTH = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom;

// Default and minimum number of days shown in the zoomed-in view -- three
// weeks is enough to read trend/slope without cramming a long sprint's
// entire history into one illegibly dense screen by default.
const DEFAULT_WINDOW = 21;
const MIN_WINDOW = 5;

// A "nice" axis step (1/2/5 × a power of 10) so gridlines land on round
// numbers rather than whatever `max / 5` happens to divide out to.
const niceStep = (roughStep: number): number => {
  if (roughStep <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
};

const formatDayLabel = (day: string) => {
  const [, month, date] = day.split("-");
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${monthNames[Number(month) - 1]} ${Number(date)}`;
};

const clampWindow = (start: number, size: number, total: number) => {
  const clampedSize = Math.min(total, Math.max(MIN_WINDOW, size));
  const clampedStart = Math.min(Math.max(0, start), Math.max(0, total - clampedSize));
  return { size: clampedSize, start: clampedStart };
};

/**
 * Hand-rolled inline SVG line chart -- deliberately not a chart library (see
 * the plan doc: no charting dependency exists in this single-bundle SPA
 * today, and one line chart with an assignee-line overlay doesn't justify
 * adding one). One <path> per series, a dashed ideal-trend reference line,
 * a shared per-day hover column for a lightweight tooltip, and a zoomable/
 * pannable "visible window" over `days` for sprints too long to read at once.
 */
export function BurndownChart({ days, series }: { days: string[]; series: BurndownSeries[] }) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [windowSize, setWindowSize] = useState(() => Math.min(days.length, DEFAULT_WINDOW));
  const [windowStart, setWindowStart] = useState(() => Math.max(0, days.length - DEFAULT_WINDOW));
  const dragRef = useRef<{ startClientX: number; startWindowStart: number } | null>(null);

  // A new sprint selection (different day range) should reset zoom/pan
  // instead of carrying over a stale window from whatever was viewed before.
  // Anchored to the *end* of the range (most recent days), not the start --
  // a burndown's most useful default view is "up to today," not the
  // beginning of a long-ago sprint.
  useEffect(() => {
    const size = Math.min(days.length, DEFAULT_WINDOW);
    setWindowSize(size);
    setWindowStart(Math.max(0, days.length - size));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days.length, days[0]]);

  const { size: clampedSize, start: clampedStart } = clampWindow(windowStart, windowSize, days.length);
  const visibleDays = useMemo(
    () => days.slice(clampedStart, clampedStart + clampedSize),
    [days, clampedStart, clampedSize],
  );
  const visibleSeries = useMemo(
    () => series.map((one) => ({ ...one, values: one.values.slice(clampedStart, clampedStart + clampedSize) })),
    [series, clampedStart, clampedSize],
  );

  // The Y axis stays scaled to the *full* series so it doesn't rescale
  // confusingly while panning -- only the X mapping is windowed.
  const maxValue = useMemo(() => {
    const allValues = series.flatMap((one) => one.values);
    return Math.max(1, ...allValues);
  }, [series]);
  const step = niceStep(maxValue / 5);
  const axisMax = Math.ceil(maxValue / step) * step;
  const gridlineValues = useMemo(() => {
    const values: number[] = [];
    for (let value = 0; value <= axisMax; value += step) values.push(value);
    return values;
  }, [axisMax, step]);

  const xForIndex = (index: number) => visibleDays.length <= 1
    ? MARGIN.left + PLOT_WIDTH / 2
    : MARGIN.left + (index / (visibleDays.length - 1)) * PLOT_WIDTH;
  const yForValue = (value: number) => MARGIN.top + PLOT_HEIGHT - (value / axisMax) * PLOT_HEIGHT;

  const pathFor = (values: number[]) =>
    values.map((value, index) => `${index === 0 ? "M" : "L"}${xForIndex(index)},${yForValue(value)}`).join(" ");

  // Thin out day labels on longer windows so they don't overlap.
  const labelEvery = Math.max(1, Math.ceil(visibleDays.length / 8));

  const isZoomed = clampedSize < days.length;
  const zoomIn = () => setWindowSize((size) => clampWindow(clampedStart, Math.max(MIN_WINDOW, Math.round(size * 0.75)), days.length).size);
  const zoomOut = () => setWindowSize((size) => clampWindow(clampedStart, Math.round(size / 0.75), days.length).size);
  const resetZoom = () => { setWindowSize(days.length); setWindowStart(0); };
  const panBy = (deltaDays: number) => setWindowStart((start) => clampWindow(start + deltaDays, clampedSize, days.length).start);
  const stepSize = () => Math.max(1, Math.floor(clampedSize / 4));

  const onPointerDown = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (!isZoomed) return;
    dragRef.current = { startClientX: event.clientX, startWindowStart: clampedStart };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onPointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (!dragRef.current) return;
    const svgRect = event.currentTarget.getBoundingClientRect();
    const pixelsPerDay = (PLOT_WIDTH * (svgRect.width / WIDTH)) / Math.max(1, clampedSize - 1 || 1);
    const deltaDays = Math.round((dragRef.current.startClientX - event.clientX) / Math.max(1, pixelsPerDay));
    setWindowStart(clampWindow(dragRef.current.startWindowStart + deltaDays, clampedSize, days.length).start);
  };
  const onPointerUp = () => { dragRef.current = null; };

  return (
    <div className="burndown-chart">
      <div className="burndown-zoom-controls">
        <button aria-label="Zoom out" disabled={!isZoomed} onClick={zoomOut} type="button">
          <i aria-hidden="true" className="bi bi-dash-lg" />
        </button>
        <button aria-label="Zoom in" disabled={clampedSize <= MIN_WINDOW} onClick={zoomIn} type="button">
          <i aria-hidden="true" className="bi bi-plus-lg" />
        </button>
        <button aria-label="Pan earlier" disabled={clampedStart <= 0} onClick={() => panBy(-stepSize())} type="button">
          <i aria-hidden="true" className="bi bi-chevron-left" />
        </button>
        <button aria-label="Pan later" disabled={clampedStart + clampedSize >= days.length} onClick={() => panBy(stepSize())} type="button">
          <i aria-hidden="true" className="bi bi-chevron-right" />
        </button>
        <button disabled={!isZoomed} onClick={resetZoom} type="button">Reset zoom</button>
      </div>
      <svg
        onPointerDown={onPointerDown}
        onPointerLeave={onPointerUp}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        role="img"
        style={isZoomed ? { cursor: dragRef.current ? "grabbing" : "grab" } : undefined}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      >
        <title>Sprint burndown</title>
        {gridlineValues.map((value) => (
          <g key={value}>
            <line
              className="burndown-gridline"
              x1={MARGIN.left} x2={WIDTH - MARGIN.right}
              y1={yForValue(value)} y2={yForValue(value)}
            />
            <text className="burndown-axis-label" textAnchor="end" x={MARGIN.left - 6} y={yForValue(value) + 4}>{value}</text>
          </g>
        ))}
        {visibleDays.map((day, index) => (index % labelEvery === 0 || index === visibleDays.length - 1) && (
          <text
            className="burndown-axis-label"
            key={day}
            textAnchor="middle"
            x={xForIndex(index)}
            y={HEIGHT - MARGIN.bottom + 18}
          >
            {formatDayLabel(day)}
          </text>
        ))}
        {visibleSeries.map((one) => (
          <path
            className="burndown-line"
            d={pathFor(one.values)}
            fill="none"
            key={one.id}
            stroke={one.color}
            strokeDasharray={one.dashed ? "6 4" : undefined}
            strokeWidth={one.dashed ? 1.5 : 2}
          />
        ))}
        {visibleDays.map((day, index) => (
          <rect
            className="burndown-hover-column"
            height={PLOT_HEIGHT}
            key={day}
            onMouseEnter={() => setHoverIndex(index)}
            onMouseLeave={() => setHoverIndex((value) => (value === index ? null : value))}
            width={PLOT_WIDTH / Math.max(1, visibleDays.length)}
            x={xForIndex(index) - PLOT_WIDTH / Math.max(1, visibleDays.length) / 2}
            y={MARGIN.top}
          />
        ))}
        {hoverIndex !== null && (
          <line
            className="burndown-hover-line"
            x1={xForIndex(hoverIndex)} x2={xForIndex(hoverIndex)}
            y1={MARGIN.top} y2={MARGIN.top + PLOT_HEIGHT}
          />
        )}
      </svg>
      <div className="burndown-legend">
        {series.map((one) => (
          <span className="burndown-legend-item" key={one.id}>
            <span className="burndown-legend-swatch" style={{ background: one.color }} />
            {one.label}
          </span>
        ))}
      </div>
      {hoverIndex !== null && visibleDays[hoverIndex] && (
        <div className="burndown-tooltip" role="status">
          <strong>{formatDayLabel(visibleDays[hoverIndex])}</strong>
          {visibleSeries.map((one) => (
            <div key={one.id}><span className="burndown-legend-swatch" style={{ background: one.color }} />{one.label}: {one.values[hoverIndex]}</div>
          ))}
        </div>
      )}
    </div>
  );
}
